import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Building2, Users, ArrowRight, MessageCircle, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import NotFound from "./NotFound";
import { CITY_PAGES } from "../data/cityPagesData";

const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "917836874807";
const PREFIX = "real-estate-leads-";

export default function CityLeadPage() {
    const { slugFull } = useParams();
    const slug = slugFull?.startsWith(PREFIX) ? slugFull.slice(PREFIX.length) : null;
    const data = slug ? CITY_PAGES[slug] : null;

    useEffect(() => {
        if (!data) return;
        const title = `Real Estate Leads in ${data.name} | Verified Buyer Leads · Lead Krafts`;
        const desc = `${data.intro.slice(0, 155)}...`;
        document.title = title;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", "description");
            document.head.appendChild(meta);
        }
        meta.setAttribute("content", desc);
        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", `https://leadkrafts.com/real-estate-leads-${slug}`);
    }, [data, slug]);

    if (!data) return <NotFound />;

    const waMsg = encodeURIComponent(
        `Hi Lead Krafts, I want to understand verified real-estate lead pricing for a project in ${data.name}.`
    );
    const waHref = `https://wa.me/${WA_NUMBER}?text=${waMsg}`;

    return (
        <div data-testid={`city-page-${slug}`} className="min-h-screen bg-[#050507] text-white">
            <Header />

            <main className="pt-28">
                {/* Hero */}
                <section className="px-6 sm:px-10 lg:px-16 pt-16 pb-20 border-b border-white/5">
                    <div className="max-w-7xl mx-auto">
                        <nav className="flex items-center gap-2 text-xs text-white/50 mb-8">
                            <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
                            <ChevronRight size={12} />
                            <span>Real Estate Leads</span>
                            <ChevronRight size={12} />
                            <span className="text-[#D4AF37]">{data.name}</span>
                        </nav>

                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-px w-10 bg-[#D4AF37]" />
                            <span className="overline">{data.region} · Lead Krafts Coverage</span>
                        </div>
                        <h1 data-testid="city-h1" className="font-serif-display text-4xl sm:text-6xl leading-[1.05] tracking-tight max-w-4xl">
                            {data.h1}
                        </h1>
                        <p className="mt-8 max-w-3xl text-white/70 text-base sm:text-lg leading-relaxed">
                            {data.intro}
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/#calculator" className="btn-gold">
                                Calculate my CPL for {data.name} <ArrowRight size={16} />
                            </Link>
                            <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-outline-gold">
                                <MessageCircle size={16} /> Chat on WhatsApp
                            </a>
                        </div>
                    </div>
                </section>

                {/* Micro-markets */}
                <section className="px-6 sm:px-10 lg:px-16 py-20 border-b border-white/5">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-px w-10 bg-[#D4AF37]" />
                            <span className="overline">Where We Run Campaigns</span>
                        </div>
                        <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-10">
                            Top micro-markets we cover in <span className="italic text-[#D4AF37]">{data.name}</span>
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {data.micro_markets.map((mm) => (
                                <div key={mm} className="hol-card p-4 flex items-center gap-3">
                                    <MapPin size={14} className="text-[#D4AF37]" strokeWidth={1.5} />
                                    <span className="text-sm text-white/80">{mm}</span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-6 text-xs text-white/40 italic">
                            Plus 40+ additional {data.name} sub-locations available on request.
                        </p>
                    </div>
                </section>

                {/* Property + Buyer segments */}
                <section className="px-6 sm:px-10 lg:px-16 py-20 border-b border-white/5">
                    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <Building2 size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
                                <span className="overline">Property Focus</span>
                            </div>
                            <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight mb-6">
                                What we generate leads for in {data.name}
                            </h3>
                            <ul className="space-y-3">
                                {data.property_focus.map((p) => (
                                    <li key={p} className="flex items-start gap-3 text-sm text-white/75">
                                        <span className="mt-1.5 h-1.5 w-1.5 bg-[#D4AF37] shrink-0" />
                                        {p}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <Users size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
                                <span className="overline">Buyer Segments</span>
                            </div>
                            <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight mb-6">
                                Who your leads come from
                            </h3>
                            <ul className="space-y-3">
                                {data.buyer_segments.map((b) => (
                                    <li key={b} className="flex items-start gap-3 text-sm text-white/75">
                                        <span className="mt-1.5 h-1.5 w-1.5 bg-[#D4AF37] shrink-0" />
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Local angle */}
                <section className="px-6 sm:px-10 lg:px-16 py-20 border-b border-white/5">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="text-[#D4AF37]/70 font-serif-display text-6xl leading-none">"</div>
                        <p className="font-serif-display text-2xl sm:text-3xl leading-[1.3] tracking-tight -mt-4">
                            {data.local_angle}
                        </p>
                        <div className="mt-6 text-xs text-white/40 uppercase tracking-[0.3em]">
                            — The Lead Krafts approach for {data.name}
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <section className="px-6 sm:px-10 lg:px-16 py-20">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="font-serif-display text-3xl sm:text-5xl leading-tight">
                            Ready to fill your {data.name} project's <span className="italic text-[#D4AF37]">pipeline</span>?
                        </h2>
                        <p className="mt-6 text-white/60">
                            Use our AI-assisted pricing engine to see an indicative CPL for your exact {data.name} micro-market,
                            property type and buyer profile. Or talk to a strategist directly on WhatsApp.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4 justify-center">
                            <Link to="/#calculator" className="btn-gold">
                                Calculate My Cost per Lead <ArrowRight size={16} />
                            </Link>
                            <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-outline-gold">
                                <MessageCircle size={16} /> Chat on WhatsApp
                            </a>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
            <FloatingWhatsApp />
        </div>
    );
}
